import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import type { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { graphql } from "@src/gql/gql";
import {
	NavBarItemType,
	NavbarSection,
	WhiteboardStatus,
} from "@src/gql/graphql";
import { handleError } from "@src/utils/error-handling";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// Define the whiteboard creation mutation
export const createWhiteboardMutation = graphql(/* GraphQL */ `
	mutation createWhiteboard($name: String!, $id: String, $correlationId: String) {
		whiteboards {
			create(name: $name, whiteboardId: $id, correlationId: $correlationId) {
				whiteboard {
					key
					name
					ownerId
					status
				}
				id
			}
		}
	}
`);

export const useCreateWhiteboard = () => {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			name,
		}: { id: WhiteboardDocumentId; name: string }) => {
			const whiteboardResult = await client.mutate({
				mutation: createWhiteboardMutation,
				variables: {
					id: id.toIdString(),
					name,
				},
			});

			const whiteboardId = whiteboardResult.data?.whiteboards?.create?.id;

			if (!whiteboardId) {
				throw new Error("Failed to create whiteboard");
			}

			return {
				data: whiteboardResult.data?.whiteboards.create,
				id: whiteboardId,
			};
		},
		onMutate: async ({ id, name }) => {
			const section = NavbarSection.MyWhiteboards;
			const queryKey = navbarBranchQueryKeys.branch(section, null);
			await queryClient.cancelQueries({ queryKey });
			const previousItems =
				queryClient.getQueryData<NavbarBranchItem[]>(queryKey);
			const boardKey = id.toIdString();

			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (oldData) => {
				const placeholder: NavbarBranchItem = {
					key: boardKey,
					name,
					type: NavBarItemType.Board,
					parentKey: null,
					rank: "",
					hasChildren: false,
					status: WhiteboardStatus.Active,
				};
				return [placeholder, ...(oldData ?? [])];
			});

			return { previousItems, queryKey, boardKey };
		},
		onSuccess: (result, _variables, context) => {
			const queryKey =
				context?.queryKey ??
				navbarBranchQueryKeys.branch(NavbarSection.MyWhiteboards, null);

			queryClient.setQueryData<NavbarBranchItem[]>(queryKey, (oldData) => {
				if (!oldData) {
					return oldData;
				}
				const confirmed: NavbarBranchItem = {
					key: result.id,
					name: result.data?.whiteboard?.name || "Untitled",
					type: NavBarItemType.Board,
					parentKey: null,
					rank: "",
					hasChildren: false,
					status: result.data?.whiteboard?.status || WhiteboardStatus.Active,
				};
				const tempKey = context?.boardKey;
				if (tempKey && tempKey !== result.id) {
					return oldData.map((item) =>
						item.key === tempKey ? confirmed : item,
					);
				}
				if (oldData.some((item) => item.key === result.id)) {
					return oldData.map((item) =>
						item.key === result.id ? confirmed : item,
					);
				}
				return [confirmed, ...oldData];
			});

			// Revalidate navbar branch in the background (do not await —
			// that would extend "Creating…" past the create mutation).
			queryClient.invalidateQueries({ queryKey });
		},
		onError: (error, _variables, context) => {
			if (context?.queryKey) {
				queryClient.setQueryData(context.queryKey, context.previousItems);
			}
			handleError(error, "Failed to create whiteboard");
		},
	});
};
